import { CalendarClock, Star } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { RelativeTime } from '@/components/custom-ui/RelativeTime'
import type { Investigation } from '@/types/investigation'
import { DetailSection } from './DetailSection'

function ActivityRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border/40 pb-2 last:border-0 last:pb-0 md:flex-nowrap">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-foreground">{value}</dd>
    </div>
  )
}

export function InvestigationActivityCard({ inv }: { inv: Investigation }) {
  const { t } = useTranslation()
  return (
    <DetailSection
      icon={<CalendarClock className="size-4" />}
      title={t('investigations.detail.activity.title')}
      hint={t('investigations.detail.activity.hint')}
    >
      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
        <ActivityRow
          label={t('investigations.detail.activity.identifier')}
          value={<span className="font-mono text-xs">{inv.id}</span>}
        />
        <ActivityRow
          label={t('investigations.detail.activity.case')}
          value={<span className="font-mono text-xs">{inv.caseId}</span>}
        />
        <ActivityRow
          label={t('investigations.detail.activity.entityCount')}
          value={<span className="font-mono tabular-nums">{inv.graphView.nodes.length}</span>}
        />
        <ActivityRow
          label={t('investigations.detail.activity.edgeCount')}
          value={<span className="font-mono tabular-nums">{inv.graphView.edges.length}</span>}
        />
        <ActivityRow
          label={t('investigations.detail.activity.noteCount')}
          value={
            <span className="font-mono tabular-nums">
              {inv.notes.filter(n => n.authorName !== '__investigation_description__').length}
            </span>
          }
        />
        <ActivityRow
          label={t('investigations.detail.activity.report')}
          value={
            inv.includedInReport ? (
              <span className="inline-flex items-center gap-1 text-primary">
                <Star className="size-3" />
                {t('investigations.detail.activity.included')}
                {inv.reportAddedAt && (
                  <span className="ml-1 font-mono text-2xs text-muted-foreground">
                    · <RelativeTime at={inv.reportAddedAt} />
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t('investigations.detail.activity.notIncluded')}
              </span>
            )
          }
        />
      </dl>
    </DetailSection>
  )
}
